import React, { useEffect, useState, useRef } from 'react';
import { api } from '../api/client';

interface MediaItem {
  id: string;
  name: string;
  type: string;
  size: string;
  url?: string;
}

export default function MediaLibrary() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function loadMedia() {
    setLoading(true);
    try {
      const res = await api.get('/api/media');
      setItems(res.data ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMedia();
  }, []);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  }

  async function handleUpload() {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      await api.upload('/api/media', formData);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadMedia();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <h1 style={styles.heading}>Media Library</h1>

      {error && (
        <div style={{ ...styles.card, padding: 20, marginBottom: 16 }}>
          <p style={{ color: '#b91c1c', fontSize: 14, margin: 0 }}>{error}</p>
        </div>
      )}

      <div style={{ ...styles.card, padding: 20, marginBottom: 16 }}>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0, marginBottom: 12 }}>
          Upload files to R2-backed media library.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            disabled={uploading}
            style={{ fontSize: 14 }}
          />
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            style={{ ...styles.button, opacity: selectedFile && !uploading ? 1 : 0.5 }}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280', fontSize: 14 }}>Loading media…</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Size</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td style={styles.td}>
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noreferrer" style={{ color: '#4F46E5', textDecoration: 'none' }}>
                      {item.name}
                    </a>
                  ) : (
                    item.name
                  )}
                </td>
                <td style={styles.td}>{item.type}</td>
                <td style={styles.td}>{item.size}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={3} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af' }}>
                  No media items
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
  card: { background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#6b7280' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  button: { padding: '8px 16px', fontSize: 14, background: '#111827', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
};
